import React from "react";
import { Input } from "@/components/atoms";
import { useCmsFormSaving } from "@/components/organisms/CmsForm";
import type { CmsFieldBaseProps } from "./CmsFieldBase";
import { CmsFieldShell } from "./CmsFieldShell";
import "./CmsField.scss";

type DateInputType = "date" | "datetime-local" | "time";

interface CmsDateFieldProps extends CmsFieldBaseProps {
  label: string;
  id: string;
  type?: DateInputType;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  variant?: "primary" | "secondary" | "danger";
  density?: "compact" | "default" | "spacious";
  borderRadius?: "sm" | "base" | "lg";
  shrink?: boolean;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  name?: string;
}

/** CMS form field wrapping Input for date-like native types. */
export function CmsDateField({
  label,
  id,
  hint,
  error,
  requiredMark,
  type = "datetime-local",
  value,
  onChange,
  variant = "primary",
  density = "default",
  borderRadius = "sm",
  shrink = false,
  disabled,
  placeholder,
  required,
  name,
  className,
  colSpan,
}: CmsDateFieldProps) {
  const isFormSaving = useCmsFormSaving();
  const isDisabled = disabled || isFormSaving;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <CmsFieldShell
      label={label}
      id={id}
      hint={hint}
      error={error}
      requiredMark={requiredMark}
      className={className}
      colSpan={colSpan}
    >
      <Input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        variant={variant}
        density={density}
        borderRadius={borderRadius}
        hasError={Boolean(error)}
        disabled={isDisabled}
        placeholder={placeholder}
        required={required}
        name={name}
        className={`cms-field__input ${shrink ? 'cms-field__input--shrink' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
      />
    </CmsFieldShell>
  );
}
